import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import BlockIcon from '@mui/icons-material/Block';
import { Box, Typography, Paper, useTheme } from '@mui/material';

import { UserApi } from '@/api';
import { Page } from '@/components';
import { IDevice } from '@/models/device';
import { EmployeeDevicesDataGrid } from '@/pages/Employees/components/EmployeeDevicesDataGrid';
import { useToastStore } from '@/store';

import {
  EmployeeDetailsOutletContext,
  getEmployeeFullName,
} from './EmployeeDetailsPage';

const NoDevicesFound = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 0',
        minHeight: '300px',
        backgroundColor: theme.palette.background.paper,
        marginTop: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <BlockIcon
          sx={{
            fontSize: 80,
            color: '#e0e0e0',
            p: 1,
            borderRadius: '50%',
            mb: 2,
          }}
        />

        <Typography variant="h6" color="text.primary" gutterBottom>
          No Devices Found
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center">
          This employee doesn&apos;t have any record of devices logged in
        </Typography>
      </Paper>
    </Box>
  );
};

export const EmployeeDevices = () => {
  const { employeeId } = useParams();
  const { updateToast } = useToastStore();
  const { employee: currentEmployee, refreshEmployee } =
    useOutletContext<EmployeeDetailsOutletContext>();

  const [devices, setDevices] = useState<IDevice[]>([]);

  const fetchDeviceData = async () => {
    if (!employeeId) {
      return;
    }

    try {
      const response = await UserApi.getUserDevices(employeeId, {});
      setDevices(response.data.results);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      setDevices([]);
    }
  };

  useEffect(() => {
    fetchDeviceData();
  }, [employeeId]);

  const employeeName = getEmployeeFullName(currentEmployee);
  const pageTitle = employeeName
    ? `Employee - ${employeeName} - ${devices.length} devices`
    : `Employee - ${devices.length} devices`;

  return (
    <Page title={pageTitle}>
      {devices.length > 0 ? (
        <EmployeeDevicesDataGrid
          employee={currentEmployee}
          refreshEmployee={refreshEmployee}
        />
      ) : (
        <NoDevicesFound />
      )}
    </Page>
  );
};
